// Date pickers (w:date) keep two things: the date itself in w:fullDate and the text
// the reader sees, written in the picker's w:dateFormat (Word's date picture, e.g.
// "dd/MM/yyyy" or "d MMMM yyyy"). The editor works with ISO dates (yyyy-mm-dd), the
// value of an <input type="date">; these helpers convert between the two.

export const DEFAULT_DATE_FORMAT = 'dd-MM-yyyy';
export const DEFAULT_DATE_LOCALE = 'nl-NL';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const FULL_DATE = /^(\d{4}-\d{2}-\d{2})/;

type Token = { readonly kind: 'd' | 'M' | 'y'; readonly length: number } | string;

/** The ISO date (yyyy-mm-dd) in `value`, or undefined when it is not a real date. */
export function toIsoDate(value: string | undefined): string | undefined {
  const match = value ? ISO_DATE.exec(value.trim()) : null;
  if (!match) {
    return undefined;
  }

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];

  return isRealDate(year, month, day) ? match[0] : undefined;
}

/** The date part of a w:fullDate value such as "1980-03-14T00:00:00Z". */
export function isoFromFullDate(fullDate: string | undefined): string | undefined {
  return toIsoDate(fullDate ? FULL_DATE.exec(fullDate)?.[1] : undefined);
}

export function formatDate(
  iso: string,
  format: string = DEFAULT_DATE_FORMAT,
  locale: string = DEFAULT_DATE_LOCALE,
): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  const name = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date);

  return tokenize(format)
    .map((token) => {
      if (typeof token === 'string') {
        return token;
      }

      switch (token.kind) {
        case 'd':
          if (token.length >= 4) return name({ weekday: 'long' });
          if (token.length === 3) return name({ weekday: 'short' });
          return pad(day, token.length);
        case 'M':
          if (token.length >= 4) return name({ month: 'long' });
          if (token.length === 3) return name({ month: 'short' });
          return pad(month, token.length);
        case 'y':
          return token.length <= 2 ? pad(year % 100, 2) : String(year);
      }
    })
    .join('');
}

/**
 * Reads a date written in `format` back to ISO. Only numeric pictures can be read;
 * text such as "Onbekend", or a format with month names, gives undefined.
 */
export function parseDate(text: string, format: string = DEFAULT_DATE_FORMAT): string | undefined {
  const tokens = tokenize(format);
  if (tokens.some((t) => typeof t !== 'string' && t.kind !== 'y' && t.length > 2)) {
    return undefined;
  }

  const order: Array<'d' | 'M' | 'y'> = [];
  const pattern = tokens
    .map((token) => {
      if (typeof token === 'string') {
        return escapeRegExp(token);
      }

      order.push(token.kind);
      if (token.kind === 'y') {
        return token.length <= 2 ? '(\\d{2})' : '(\\d{4})';
      }

      return '(\\d{1,2})';
    })
    .join('');

  const match = new RegExp(`^\\s*${pattern}\\s*$`).exec(text);
  if (!match) {
    return undefined;
  }

  const parts: Record<'d' | 'M' | 'y', number> = { d: 0, M: 0, y: 0 };
  order.forEach((kind, i) => {
    const raw = match[i + 1]!;
    parts[kind] = kind === 'y' && raw.length === 2 ? fullYear(Number(raw)) : Number(raw);
  });

  return toIsoDate(`${String(parts.y).padStart(4, '0')}-${pad(parts.M, 2)}-${pad(parts.d, 2)}`);
}

// Word date pictures: runs of d, M and y are fields, text in single quotes is
// literal ('' is a quote), everything else is copied as it is.
function tokenize(format: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < format.length) {
    const ch = format[i]!;

    if (ch === "'") {
      const end = format.indexOf("'", i + 1);
      const literal = end === -1 ? format.slice(i + 1) : format.slice(i + 1, end);
      tokens.push(literal === '' ? "'" : literal);
      i = end === -1 ? format.length : end + 1;
      continue;
    }

    if (ch === 'd' || ch === 'M' || ch === 'y') {
      let length = 1;
      while (format[i + length] === ch) {
        length++;
      }
      tokens.push({ kind: ch, length });
      i += length;
      continue;
    }

    tokens.push(ch);
    i++;
  }

  return tokens;
}

function isRealDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

// Two-digit years: 00-49 is this century, 50-99 the previous one.
function fullYear(twoDigits: number): number {
  return twoDigits < 50 ? 2000 + twoDigits : 1900 + twoDigits;
}

function pad(value: number, length: number): string {
  return String(value).padStart(Math.min(length, 2), '0');
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
